import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getVenue, updateVenue, getYemeksepetiOrders } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Save, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";

/**
 * Yemeksepeti entegrasyon sayfası — beta.
 *
 * Admin paneli üzerinden `venue.yemeksepetiEnabled` true olarak set
 * edilmiş restoranlarda dashboard sidebar'ında "Yemeksepeti" linki
 * belirir ve buraya yönlendirir.
 *
 * İki bölüm:
 *  1. Credentials formu — restoran sahibi GO panel'inden aldığı
 *     `apiKey` ve (varsa) `restaurantId`'yi kaydeder
 *  2. "Siparişleri Çek" — mevcut credentials ile backend'e
 *     `GET /yemeksepeti/orders` çağrısı, ham yanıtı listeler
 */
export function YemeksepetiPage() {
  const { venueId } = useParams<{ venueId: string }>();
  const qc = useQueryClient();

  const { data: venue, isLoading } = useQuery({
    queryKey: ["venue", venueId],
    queryFn: () => getVenue(venueId!),
    enabled: !!venueId,
  });

  const [apiKey, setApiKey] = useState("");
  const [restaurantId, setRestaurantId] = useState("");
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    if (venue?.yemeksepetiConfig) {
      setApiKey(venue.yemeksepetiConfig.apiKey ?? "");
      setRestaurantId(venue.yemeksepetiConfig.restaurantId ?? "");
    }
  }, [venue]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateVenue(venueId!, {
        // Cast — Venue tipi yemeksepetiConfig'i taşır ama updateVenue
        // signature genel Partial<Venue>. Backend Zod schema doğrular.
        yemeksepetiConfig: { apiKey, restaurantId },
      } as Parameters<typeof updateVenue>[1]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["venue", venueId] });
      setSaveOk(true);
      setTimeout(() => setSaveOk(false), 2500);
    },
  });

  const ordersQuery = useQuery({
    queryKey: ["yemeksepeti-orders", venueId],
    queryFn: () => getYemeksepetiOrders(venueId!),
    enabled: false, // Sadece "Çek" butonuna basınca tetiklenir
    retry: false,
  });

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">Yükleniyor…</div>;
  }

  if (!venue?.yemeksepetiEnabled) {
    return (
      <div className="max-w-md mx-auto py-12">
        <Card>
          <CardContent className="pt-6 text-center">
            <AlertCircle className="mx-auto mb-3 size-10 text-amber-500" />
            <p className="font-semibold">Yemeksepeti entegrasyonu kapalı</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Bu işletme için Yemeksepeti henüz açık değil. HereMenu yöneticisiyle iletişime geçin.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fetchErr = ordersQuery.error as { response?: { data?: { error?: string; message?: string; status?: number } }; message?: string } | null;
  const fetchErrMsg = fetchErr?.response?.data?.message
    || fetchErr?.response?.data?.error
    || fetchErr?.message;

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Yemeksepeti</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Yemek Sepeti GO panelinden aldığınız API key ile bağlantı kurun.
        </p>
      </div>

      {/* Credentials */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bağlantı Bilgileri</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>API Key</Label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="GO panelinden alınan key"
              autoComplete="off"
            />
          </div>
          <div>
            <Label>Restoran ID (opsiyonel)</Label>
            <Input
              value={restaurantId}
              onChange={(e) => setRestaurantId(e.target.value)}
              placeholder="GO panelindeki restoran ID"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || !apiKey}>
              <Save className="mr-2 size-4" />
              {saveMutation.isPending ? "Kaydediliyor…" : "Kaydet"}
            </Button>
            {saveOk && (
              <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
                <CheckCircle2 className="size-4" /> Kaydedildi
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test fetch */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Sipariş Çekme (Test)</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => ordersQuery.refetch()}
            disabled={ordersQuery.isFetching || !venue?.yemeksepetiConfig?.apiKey}
          >
            {ordersQuery.isFetching
              ? <><Loader2 className="mr-2 size-4 animate-spin" />Çekiliyor…</>
              : <><RefreshCw className="mr-2 size-4" />Çek</>}
          </Button>
        </CardHeader>
        <CardContent>
          {!venue?.yemeksepetiConfig?.apiKey && (
            <p className="text-sm text-muted-foreground">Önce API key kaydedin.</p>
          )}
          {fetchErrMsg && (
            <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
              <p className="font-medium">Bağlantı hatası</p>
              <p className="mt-1 break-words">{fetchErrMsg}</p>
            </div>
          )}
          {ordersQuery.data && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                {ordersQuery.data.length} sipariş bulundu.
              </p>
              {ordersQuery.data.length === 0 ? (
                <p className="text-sm">Aktif sipariş yok.</p>
              ) : (
                <pre className="max-h-96 overflow-auto rounded-md border bg-muted/30 p-3 text-xs">
                  {JSON.stringify(ordersQuery.data, null, 2)}
                </pre>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
